import { useLang } from 'hooks/lang';

import { User } from 'entities';

import { actions } from '../../modules';

import { useJustification } from 'providers/Justification';
import { useOwnershipChange } from 'providers/OwnershipChange';
import { getFirst } from 'utils/array';
import { DEFAULT_NO_OWNER_USER } from 'utils/constants';

type TargetProject = {
  id: string;
  owner: User;
};

type IfProjectUndefined<
  SingleProject extends TargetProject | undefined,
  Y,
  N,
> = undefined extends SingleProject ? Y : N;

type ProjectTeamChangeHookProps<SingleProject extends TargetProject | undefined> = {
  onAccept?: (newOwner: User) => void;
  onReject?: () => void;
  onFinish?: (projectId: string) => void;
} & IfProjectUndefined<SingleProject, { project?: never }, { project: SingleProject }>;

type ActionParameters<SingleProject extends TargetProject | undefined> = IfProjectUndefined<
  SingleProject,
  [TargetProject],
  []
>;

interface ProjectTeamChangeHookResult<SingleProject extends TargetProject | undefined> {
  rejectRequest: (...params: ActionParameters<SingleProject>) => void;
  acceptRequest: (...params: ActionParameters<SingleProject>) => void;
  loading: boolean;
}

export function useProjectTeamChangeResponse<SingleProject extends TargetProject | undefined>(
  {
    project,
    onAccept,
    onReject,
    onFinish,
  }: ProjectTeamChangeHookProps<SingleProject> = {} as ProjectTeamChangeHookProps<SingleProject>,
): ProjectTeamChangeHookResult<SingleProject> {
  const { t } = useLang();

  const answerRequest = actions.projects.answerTeamChange.use({
    onSuccess: (_, { projectId }) => onFinish?.(projectId),
  });

  const { openJustificationModal } = useJustification();

  const rejectRequest = (...params: ActionParameters<SingleProject>): void => {
    const projectRef = project ?? getFirst(params);

    openJustificationModal({
      text: '',

      onConfirm: async (justification) => {
        const result = await answerRequest.execute({
          approved: false,
          justification,
          projectId: projectRef.id,
        });

        if (!result) return;

        onReject?.();
      },
    });
  };

  const { openChangeOwnerModal } = useOwnershipChange();

  const acceptRequest = (...params: ActionParameters<SingleProject>): void => {
    const projectRef = project ?? getFirst(params);

    openChangeOwnerModal({
      currentOwner: projectRef.owner ?? DEFAULT_NO_OWNER_USER,
      text: t('assign-to-your-team'),
      requireJustification: false,
      onConfirm: async (newOwner) => {
        const result = await answerRequest.execute({
          approved: true,
          newOwner: newOwner!.id,
          projectId: projectRef.id,
        });

        if (!result) return;

        onAccept?.(newOwner!);
      },
    });
  };

  return {
    rejectRequest,
    acceptRequest,
    loading: answerRequest.loading,
  };
}
